"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Treatment } from "@/lib/treatments-data";
import { useTranslation } from "@/hooks/useTranslation";
import Header from "@/components/layout/header";
import Breadcrumbs from "@/components/Breadcrumbs";
import HollywoodSmileHeroSection from "./hollywood-smile/hero-section";
import WhatIsHollywoodSmileSection from "./hollywood-smile/what-is-section";
import WhyChooseDentaWistaSection from "./hollywood-smile/why-choose-section";
import SmileJourneySection from "./hollywood-smile/smile-journey-section";
import ProceduresSection from "./hollywood-smile/procedures-section";
import GallerySection from "./hollywood-smile/gallery-section";
import FAQSection from "./hollywood-smile/faq-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";

gsap.registerPlugin(ScrollTrigger);

interface HollywoodSmilePageProps {
  treatment: Treatment;
}

export default function HollywoodSmilePage({
  treatment,
}: HollywoodSmilePageProps) {
  const { t, locale } = useTranslation('hollywood-smile');
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const sections = [
    { id: "hero", label: t('navigation.sections.home') },
    { id: "what-is", label: t('navigation.sections.whatIs') },
    { id: "why-choose", label: t('navigation.sections.whyChoose') },
    { id: "journey", label: t('navigation.sections.journey') },
    { id: "procedures", label: t('navigation.sections.procedures') },
    { id: "gallery", label: t('navigation.sections.gallery') },
    { id: "faq", label: t('navigation.sections.faq') },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">
      <Header />

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('navigation.backToTreatments')}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={sections} />

      <main className="relative">
        <HollywoodSmileHeroSection treatment={treatment} />
        <WhatIsHollywoodSmileSection />
        <WhyChooseDentaWistaSection />
        <SmileJourneySection />
        <ProceduresSection />
        <GallerySection />
        <FAQSection />
        <FinalCTASection
          treatmentName={t('cta.default_heading_part2')}
          treatmentKeyword="hollywood smile"
          scrollToSection="faq"
          customHeading={
            <>
              {t('cta.default_heading_part1')}{" "}
              <span className="text-gold">{t('cta.default_heading_part2')}</span>
            </>
          }
          customSubheading={t('cta.default_subheading').replace('{{treatment}}', 'Hollywood Smile')}
          customBenefits={[
            t('cta.default_benefits.0'),
            t('cta.default_benefits.1'),
            t('cta.default_benefits.2'),
            t('cta.default_benefits.3'),
          ]}
        />
      </main>

    </div>
  );
}
